import { Outlet, useParams } from "react-router-dom";
import { useMemo } from "react";
import { tokenRowFilters, usePreviewStore } from "@/lib/preview/previewStore";
import { computeLegacyFloor } from "@/lib/authz/capabilities";
import { CapabilitiesProvider } from "@/providers/CapabilitiesProvider";
import { RolePreviewBanner } from "@/components/PBanner/RolePreviewBanner";
import { PreviewReadOnlyDialog } from "@/components/PBanner/PreviewReadOnlyDialog";
import { PreviewEndedNotice } from "@/components/PBanner/PreviewEndedNotice";
import type { EventRoleDTO } from "@/types/EventRoleDTO";

/**
 * Wraps the builder route group. Builders live OUTSIDE EventLayoutShell, so when
 * one is opened during "preview as role" — a new tab that inherited the
 * read-only bearer via the sessionStorage handoff in previewStore — it would
 * otherwise have no preview banner and no CapabilitiesProvider, so write
 * controls couldn't gate and the user couldn't tell they're in a read-only
 * preview.
 *
 * When an active preview matches the builder's {eventId}, this renders the same
 * RolePreviewBanner used by EventLayoutShell and provides a CapabilitiesProvider
 * scoped to the previewed role (so useCapabilities()/useCapabilitiesOptional()
 * inside builders reflect that role). For normal admins (no active preview) it's
 * a transparent pass-through — no extra provider, no banner.
 *
 * B2-0: ONE `PreviewEndedNotice` sits above BOTH branches, before the outlet.
 * When the preview ends locally (a revoked or expired bearer) this chrome falls
 * back to the bare pass-through and the builder remounts as the ADMIN — the
 * notice blocks that tab until a reload, so a "Save" never silently runs on the
 * admin cookie. Being the first child of the same fragment in both branches, the
 * branch switch does not remount it.
 */
export function BuilderPreviewChrome() {
  const { eventId } = useParams<{ eventId: string }>();
  const active = usePreviewStore((s) => s.active);
  const previewEventId = usePreviewStore((s) => s.eventId);
  const target = usePreviewStore((s) => s.target);
  // The EDITED chrome (the banner here has the editor too), like the shell —
  // not the start snapshot `target.*`. Row filters: what the token applies,
  // by reference (M4).
  const effectiveCaps = usePreviewStore((s) => s.effectiveCaps);
  const effectiveFieldAllowlist = usePreviewStore((s) => s.effectiveFieldAllowlist);
  const rowFilters = usePreviewStore(tokenRowFilters);

  const previewing =
    active && !!target && !!eventId && previewEventId === eventId;

  const effectiveRole = useMemo<EventRoleDTO | null>(() => {
    if (!previewing || !target) return null;
    const isBase = target.kind === "baseRole";
    const caps = effectiveCaps.length > 0 ? effectiveCaps : target.capabilities;
    return {
      // Only role/caps/scope are consumed by CapabilitiesProvider; the rest are
      // required-but-unused identity fields.
      eventRoleId: "",
      name: target.name,
      iconColor: "",
      email: "",
      role: isBase ? (target.baseRole as string) : "CUSTOM",
      valid: true,
      templateId: target.kind === "template" ? target.templateId : undefined,
      capabilities: caps,
      fieldAllowlist: effectiveFieldAllowlist,
      rowFilters,
      legacyRoleFloor: isBase ? undefined : computeLegacyFloor(new Set(caps)),
    };
  }, [previewing, target, effectiveCaps, effectiveFieldAllowlist, rowFilters]);

  const notice = <PreviewEndedNotice host="builder" eventId={eventId ?? null} />;

  if (previewing && effectiveRole) {
    return (
      <>
        {notice}
        <CapabilitiesProvider eventRole={effectiveRole}>
          {/* Fixed overlay so the builder's own full-height layout is untouched. */}
          <div className="fixed top-0 inset-x-0 z-[10001]">
            <RolePreviewBanner />
          </div>
          {/* Builders live in their own tab outside EventLayoutShell, so the
              read-only notice must be mounted here too — otherwise a masked save
              (403) in a builder would pop nothing and the builder's own "unknown
              error" toast would be the only signal. */}
          <PreviewReadOnlyDialog />
          <Outlet />
        </CapabilitiesProvider>
      </>
    );
  }
  return (
    <>
      {notice}
      <Outlet />
    </>
  );
}
